import role0Schema from '../../assets/forms/role-0.form.json';
import role1Schema from '../../assets/forms/role-1.form.json';
import role7Schema from '../../assets/forms/role-130000.form.json';

import type { InputTransform } from './input-transform.util';

/**
 * Schema-derived value normalisation rules.
 *
 * ## Why this exists
 *
 * A field's input behaviour is declared once, in its sheet JSON — e.g. the BRN
 * carries `ui.trimLeadingZeros: true`. The UI seam (utils/input-filter.ts) reads
 * that flag straight off the rendered control. The state seam
 * (FilingFormStateService.applyDefaultValuesToState) has no control to read
 * from: it is a hardcoded qname dictionary that runs on raw state blobs.
 *
 * Without this file, the state seam would need its own hardcoded copy of the
 * BRN qname — one rule, two mechanisms, drifting independently. That is exactly
 * the failure mode schema-min.util.ts / schema-validators.util.ts were written
 * to end for numeric constraints. This file does the same job for normalisers:
 * the JSON flag stays the single source of truth, and the state seam asks the
 * schema rather than repeating it.
 *
 * ## Why a static import
 *
 * The sheet components fetch their schema at runtime, but state writes happen
 * before either sheet mounts — IndexedDB rehydrate and cross-tab sync both run
 * at app start. A runtime registry populated on component init would therefore
 * miss those early writes intermittently. Importing the JSON at build time makes
 * the set available synchronously from the first state write onward. Cost: the
 * two schemas (~39 KB raw) are bundled as well as fetched. `resolveJsonModule`
 * is enabled in tsconfig.json.
 *
 * ## Scope
 *
 * Covers role0 and role1 (the BRN's `trimLeadingZeros`) and role7 (the audit firm
 * registration number's `inputTransform`). A further sheet adopting a normaliser
 * flag must be added to the import list below; the flag alone will not reach the
 * state seam.
 */

/**
 * Minimal structural view of a sheet schema. The JSON modules' inferred types
 * vary control-by-control (each `ui` object has a different key set), so they
 * are widened through `unknown` rather than fought with.
 */
interface NormalizerFlagSource {
  controls?: Array<{
    qname?: string;
    ui?: { trimLeadingZeros?: boolean; inputTransform?: InputTransform[]; maxLength?: number };
  }>;
}

const SCHEMAS: readonly NormalizerFlagSource[] = [
  role0Schema as unknown as NormalizerFlagSource,
  role1Schema as unknown as NormalizerFlagSource,
  role7Schema as unknown as NormalizerFlagSource,
];

function deriveTrimQnames(schemas: readonly NormalizerFlagSource[]): ReadonlySet<string> {
  const qnames = new Set<string>();
  for (const schema of schemas) {
    for (const control of schema.controls ?? []) {
      if (control.ui?.trimLeadingZeros === true && control.qname) {
        qnames.add(control.qname);
      }
    }
  }
  return qnames;
}

/**
 * Qnames whose schema declares `ui.trimLeadingZeros`. Derived once at module
 * load; the schemas are build-time constants, so there is nothing to recompute.
 */
export const TRIM_LEADING_ZERO_QNAMES: ReadonlySet<string> = deriveTrimQnames(SCHEMAS);

/** A control's `ui.inputTransform` list plus the `ui.maxLength` cap applied after it. */
export interface InputTransformRule {
  readonly transforms: readonly InputTransform[];
  readonly maxLength?: number;
}

function deriveInputTransforms(
  schemas: readonly NormalizerFlagSource[]
): ReadonlyMap<string, InputTransformRule> {
  const rules = new Map<string, InputTransformRule>();
  for (const schema of schemas) {
    for (const control of schema.controls ?? []) {
      const transforms = control.ui?.inputTransform;
      if (control.qname && transforms?.length) {
        rules.set(control.qname, { transforms, maxLength: control.ui?.maxLength });
      }
    }
  }
  return rules;
}

/**
 * Qnames whose schema declares `ui.inputTransform` (e.g. strip whitespace +
 * uppercase), with their transform list and length cap. Same derive-once,
 * schema-is-the-single-source-of-truth design as {@link TRIM_LEADING_ZERO_QNAMES}.
 */
export const INPUT_TRANSFORMS_BY_QNAME: ReadonlyMap<string, InputTransformRule> =
  deriveInputTransforms(SCHEMAS);
